<template>
  <div class="support-widget">
    <!-- Floating Help Button -->
    <transition name="slide-fade">
      <v-btn
        v-if="!isExpanded"
        class="help-button"
        color="primary"
        elevation="4"
        @click="toggleWidget"
      >
        <span class="vertical-text">HELP</span>
      </v-btn>
    </transition>

    <!-- Expanded Widget Panel -->
    <transition name="slide-in">
      <v-card
        v-if="isExpanded"
        class="support-panel"
        elevation="8"
      >
        <!-- Header -->
        <v-card-title class="support-header d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-help-circle</v-icon>
            <span>Support & Help</span>
          </div>
          <v-btn
            icon
            size="small"
            variant="text"
            @click="closeWidget"
          >
            <v-icon color="primary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Content -->
        <v-card-text class="support-content pa-0">
          <!-- FAQ View -->
          <div v-if="currentView === 'faq'" class="pa-4">
            <h3 class="text-h6 mb-4">Frequently Asked Questions</h3>

            <v-expansion-panels variant="accordion">
              <v-expansion-panel
                v-for="(faq, index) in faqs"
                :key="index"
              >
                <v-expansion-panel-title>
                  {{ faq.question }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  {{ faq.answer }}
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-btn
              color="primary"
              variant="elevated"
              block
              class="mt-6"
              prepend-icon="mdi-email"
              @click="showContactForm"
            >
              Contact Us
            </v-btn>
          </div>

          <!-- Contact Form View -->
          <div v-if="currentView === 'contact'" class="pa-4">
            <v-btn
              variant="text"
              size="small"
              prepend-icon="mdi-arrow-left"
              class="mb-4"
              @click="backToFaq"
            >
              Back to FAQ
            </v-btn>

            <h3 class="text-h6 mb-4">Contact Us</h3>

            <v-form ref="contactFormRef" @submit.prevent="submitForm">
              <v-text-field
                v-model="form.name"
                label="Name *"
                :rules="[rules.required]"
                variant="outlined"
                density="comfortable"
                class="mb-3"
              ></v-text-field>

              <v-text-field
                v-model="form.email"
                label="Email Address *"
                :rules="[rules.required, rules.email]"
                variant="outlined"
                density="comfortable"
                class="mb-3"
                type="email"
              ></v-text-field>

              <v-textarea
                v-model="form.message"
                label="Your Message/Feedback *"
                :rules="[rules.required]"
                variant="outlined"
                rows="5"
                class="mb-3"
              ></v-textarea>

              <v-file-input
                v-model="form.files"
                label="Attach Photo or Video (Optional)"
                variant="outlined"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                accept="image/*,video/*"
                :rules="[rules.fileSize]"
                show-size
                class="mb-3"
                @update:model-value="validateFiles"
              >
                <template v-slot:selection="{ fileNames }">
                  <template v-for="fileName in fileNames" :key="fileName">
                    <v-chip
                      size="small"
                      label
                      color="primary"
                      class="mr-2"
                    >
                      {{ fileName }}
                    </v-chip>
                  </template>
                </template>
              </v-file-input>

              <v-alert
                v-if="fileError"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                {{ fileError }}
              </v-alert>

              <!--
              <div class="recaptcha-notice mb-4">
                <v-icon size="small" class="mr-1">mdi-shield-check</v-icon>
                <span class="text-caption">This form is protected by reCAPTCHA</span>
              </div>
              -->

              <v-btn
                type="submit"
                color="primary"
                variant="elevated"
                block
                :loading="submitting"
                :disabled="submitting"
              >
                Submit
              </v-btn>
            </v-form>
          </div>

          <!-- Confirmation View -->
          <div v-if="currentView === 'confirmation'" class="pa-4 text-center">
            <v-icon
              size="80"
              color="success"
              class="mb-4"
            >
              mdi-check-circle
            </v-icon>

            <h3 class="text-h6 mb-3">Thank You!</h3>

            <p class="text-body-1 mb-4">
              Your support request has been submitted successfully.
              We will review your feedback and send you an email response
              within approximately 1-7 days.
            </p>

            <v-btn
              color="primary"
              variant="elevated"
              @click="resetWidget"
            >
              Close
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </transition>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useMemorialStore } from '../../stores/memorial'
import apiService from '../../services/api'

const store = useMemorialStore()

// State
const isExpanded = ref(false)
const currentView = ref('faq') // 'faq', 'contact', 'confirmation'
const submitting = ref(false)
const contactFormRef = ref(null)
const fileError = ref('')

// Form data
const form = ref({
  name: '',
  email: '',
  message: '',
  files: []
})

// Validation rules
const rules = {
  required: value => !!value || 'This field is required',
  email: value => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return pattern.test(value) || 'Please enter a valid email address'
  },
  fileSize: () => {
    return true // Handled separately in validateFiles
  }
}

// FAQ data
const faqs = ref([
  {
    question: 'How do I create a memory?',
    answer: 'Click on "Create Memory" on the landing page and follow the step-by-step process. You\'ll add photos, write stories, record a message, and invite loved ones to contribute.'
  },
  {
    question: 'Can I edit my memory after creating it?',
    answer: 'Yes! Once you\'re logged in, go to your memory dashboard where you can edit details, photos, stories, messages, and manage contacts at any time.'
  },
  {
    question: 'How do I share my memory with others?',
    answer: 'From your memory dashboard, go to "Edit Contacts and Share" to add email addresses. You can also copy the shareable link to distribute via social media or messaging apps.'
  },
  {
    question: 'What file formats are supported for photos and videos?',
    answer: 'We support common image formats (JPG, PNG, HEIC) and video formats (MP4, MOV). Photos are automatically converted and optimized for web viewing.'
  },
  {
    question: 'Can others contribute to my memory?',
    answer: 'Yes! People you share the memory with can add their own photos, videos, and messages. You\'ll receive notifications when new contributions are added.'
  },
  {
    question: 'How do I manage notification settings?',
    answer: 'Go to your Profile page from the top-right menu and adjust your notification preferences, including contribution notifications and email settings.'
  },
  {
    question: 'Is my memory private or public?',
    answer: 'Your memory is private by default and only accessible via the shareable link. Only people you share the link with can view and contribute to it.'
  },
  {
    question: 'How do I download or print my memory?',
    answer: 'From your memory dashboard, you can export to PDF or create a video slideshow. These can be downloaded and printed or shared offline.'
  }
])

// Methods
const toggleWidget = () => {
  isExpanded.value = !isExpanded.value
}

const closeWidget = () => {
  isExpanded.value = false
  setTimeout(() => {
    currentView.value = 'faq'
    resetForm()
  }, 300)
}

const showContactForm = () => {
  currentView.value = 'contact'

  // Pre-populate email if user is logged in
  if (store.apiToken && store.userEmail) {
    form.value.email = store.userEmail
  }

  // Pre-populate name if available
  if (store.user?.firstName) {
    form.value.name = store.user.firstName
    if (store.user?.surname) {
      form.value.name += ' ' + store.user.surname
    }
  }
}

const backToFaq = () => {
  currentView.value = 'faq'
  resetForm()
}

const validateFiles = (files) => {
  fileError.value = ''

  if (!files || files.length === 0) {
    return
  }

  const file = files[0]

  // Handle case where files array exists but first element is undefined
  if (!file) {
    return
  }

  const isImage = file.type?.startsWith('image/')
  const isVideo = file.type?.startsWith('video/')

  const maxImageSize = 10 * 1024 * 1024 // 10MB
  const maxVideoSize = 20 * 1024 * 1024 // 20MB

  if (isImage && file.size > maxImageSize) {
    fileError.value = 'Image file must be less than 10MB'
    form.value.files = []
  } else if (isVideo && file.size > maxVideoSize) {
    fileError.value = 'Video file must be less than 20MB'
    form.value.files = []
  } else if (!isImage && !isVideo) {
    fileError.value = 'Please upload only image or video files'
    form.value.files = []
  }
}

// const loadRecaptcha = () => {
//   return new Promise((resolve, reject) => {
//     if (window.grecaptcha) {
//       resolve()
//       return
//     }
//
//     const script = document.createElement('script')
//     script.src = 'https://www.google.com/recaptcha/api.js?render=6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI' // Using Google's test key
//     script.onload = resolve
//     script.onerror = reject
//     document.head.appendChild(script)
//   })
// }
//
// const executeRecaptcha = async () => {
//   try {
//     await loadRecaptcha()
//
//     return new Promise((resolve, reject) => {
//       window.grecaptcha.ready(() => {
//         window.grecaptcha.execute('6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', { action: 'submit' })
//           .then(token => resolve(token))
//           .catch(err => reject(err))
//       })
//     })
//   } catch (error) {
//     console.error('reCAPTCHA error:', error)
//     throw error
//   }
// }

const submitForm = async () => {
  const { valid } = await contactFormRef.value.validate()

  if (!valid || fileError.value) {
    return
  }

  submitting.value = true

  try {
    // Execute reCAPTCHA
    // const recaptchaToken = await executeRecaptcha()

    // Prepare form data
    const formData = new FormData()
    formData.append('name', form.value.name)
    formData.append('email', form.value.email)
    formData.append('message', form.value.message)
    // formData.append('recaptchaToken', recaptchaToken)

    // Add file if present
    if (form.value.files && form.value.files.length > 0) {
      formData.append('file', form.value.files[0])
    }

    // Submit to backend API
    const response = await apiService.submitSupportTicket(formData)

    if (response.success) {
      console.log('Support ticket submitted successfully:', response.ticketUuid)
      // Show confirmation
      currentView.value = 'confirmation'
    } else {
      // Handle error from backend
      fileError.value = response.msg || 'Failed to submit support ticket. Please try again.'
    }

  } catch (error) {
    console.error('Error submitting support ticket:', error)
    fileError.value = 'Failed to submit. Please try again.'
  } finally {
    submitting.value = false
  }
}

const resetForm = () => {
  form.value = {
    name: '',
    email: '',
    message: '',
    files: []
  }
  fileError.value = ''
  if (contactFormRef.value) {
    contactFormRef.value.reset()
  }
}

const resetWidget = () => {
  resetForm()
  currentView.value = 'faq'
  isExpanded.value = false
}

// onMounted(() => {
//   // Pre-load reCAPTCHA script
//   loadRecaptcha().catch(err => {
//     console.warn('Failed to pre-load reCAPTCHA:', err)
//   })
// })
</script>

<style scoped lang="scss">
@use '../../styles/support-widget.scss';
</style>
